import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { projekte, kategorien } from "@/data/projekte";
import { T } from "@/components/T";
import { useLanguage } from "@/contexts/LanguageContext";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

export default function Projekte() {
  const [activeFilter, setActiveFilter] = useState("Alle");
  useReveal();
  const { lang } = useLanguage();
  const filterLabel = lang === "it" ? "Tutti" : "Alle";

  const katLabelsIT = {
    'Alle': 'Tutti', 'Aufstockung': 'Sopraelevazione', 'Dachstuhl': 'Capriate',
    'Holzhaus': 'Casa in Legno', 'Hotelbau': 'Alberghiero', 'Sanierung': 'Risanamento',
    'Holzfassaden': 'Facciate', 'Gewerbe': 'Commerciale'
  };

  const filtered = activeFilter === "Alle" || activeFilter === "Tutti"
    ? projekte
    : projekte.filter(p => p.tags.includes(activeFilter) || p.kategorie === activeFilter);

  return (
    <div className="pt-20 md:pt-24">
      <section className="section-gap bg-hb-off-white border-b border-hb-border">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-6 reveal"><T de="Referenzen" it="Riferimenti" /></span>
          <h1 className="section-title max-w-2xl mb-6 reveal reveal-delay-1">
            <T de="Projekte, die bleiben." it="Progetti che durano." />
          </h1>
          <p className="section-subtitle max-w-xl reveal reveal-delay-2">
            <T de="Kein Bilderalbum – echte Projekte. Mit Kontext, Herausforderung und Ergebnis."
               it="Non un album fotografico – progetti reali. Con contesto, sfida e risultato." />
          </p>
        </div>
      </section>

      <section className="py-8 bg-white border-b border-hb-border sticky top-16 md:top-20 z-30" data-testid="projekte-filter">
        <div className="container-hb">
          <div className="flex items-center gap-3 overflow-x-auto pb-1 scrollbar-hide">
            {kategorien.map((kat) => {
              const displayKat = lang === 'it' ? (katLabelsIT[kat] || kat) : (kat === "Alle" ? filterLabel : kat);
              const isActive = kat === activeFilter || (kat === "Alle" && (activeFilter === "Alle" || activeFilter === "Tutti"));
              return (
                <button key={kat} data-testid={`filter-${kat}`}
                  onClick={() => setActiveFilter(kat)}
                  className={`flex-shrink-0 px-5 py-2 text-xs tracking-widest uppercase font-semibold transition-all duration-200 ${
                    isActive ? 'bg-hb-dark text-white' : 'bg-transparent border border-hb-border text-hb-text hover:border-hb-dark'
                  }`}>
                  {displayKat}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="section-gap bg-white" data-testid="projekte-grid">
        <div className="container-hb">
          {filtered.length === 0 ? (
            <p className="text-hb-muted text-center py-20">
              <T de="Keine Projekte in dieser Kategorie." it="Nessun progetto in questa categoria." />
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-hb-border">
              {filtered.map((p, i) => (
                <Link key={p.id} to={`/projekte/${p.id}`} data-testid={`projekt-${p.id}`}
                  className={`group bg-white card-hover overflow-hidden reveal reveal-delay-${(i % 3) + 1}`}>
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img src={p.image} alt={lang === "it" && p.it?.title ? p.it.title : p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 text-hb-dark text-[10px] tracking-widest uppercase font-semibold px-3 py-1.5">
                        {p.kategorie}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h2 className="font-manrope font-bold text-hb-dark mb-1 group-hover:text-hb-wood transition-colors">
                      {lang === "it" && p.it?.title ? p.it.title : p.title}
                    </h2>
                    <p className="text-xs text-hb-muted font-inter mb-3">{p.ort} · {p.jahr}</p>
                    <p className="text-sm text-hb-text font-inter">
                      {lang === "it" && p.it?.kurztext ? p.it.kurztext : p.kurztext}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-hb-off-white border-t border-hb-border" data-testid="projekte-cta">
        <div className="container-hb text-center">
          <h2 className="text-3xl font-manrope font-extrabold text-hb-dark mb-4">
            <T de="Ihr Projekt könnte hier stehen." it="Il vostro progetto potrebbe essere qui." />
          </h2>
          <p className="text-hb-text font-inter mb-8">
            <T de="Wir freuen uns auf Ihr Vorhaben." it="Siamo lieti di ricevere la vostra richiesta." />
          </p>
          <Link to="/kontakt" data-testid="projekte-cta-btn" className="btn-primary">
            <T de="Jetzt anfragen" it="Richiedi ora" />
          </Link>
        </div>
      </section>
    </div>
  );
}
